import { Box, Button, Flex, ScrollArea, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { CalendarAccountsPreview } from 'src/meetings/components/CalendarAccountsPreview';
import { EventsList } from 'src/meetings/components/EventsList';
import { EventsLoadingBanner } from 'src/meetings/components/EventsLoadingBanner';
import { UpcomingEventCard } from 'src/meetings/components/UpcomingEventCard';
import { useCalendarEvents } from 'src/meetings/hooks/useCalendarEvents';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import { useCalendars } from '../hooks/useCalendars';

type Props = {
  tabName: string;
  onTabChange: (tab: string) => void;
  selectedEventId: string | null;
  onEventSelect: (eventId: string | null) => void;
  onRefetchNeeded: () => void;
  tabNameConstants: {
    ALL: string;
    CLIENT: string;
  };
};

export const UpcomingEventsPanel = ({
  tabName,
  onTabChange,
  selectedEventId,
  onEventSelect,
  onRefetchNeeded,
  tabNameConstants,
}: Props) => {
  const { connecting } = useCalendars();
  const { events, hasNextPage, fetchNextPage, isFetchingNextPage } = useCalendarEvents({
    pastOrUpcoming: 'UPCOMING',
    clientMeetingsOnly: tabName === tabNameConstants.CLIENT,
  });

  return (
    <>
      <Flex align="center" gap="xs" mb="md" p="lg" pb={0}>
        <Text size="lg" fw={FONT_WEIGHTS.semibold} c="gray.10">
          Upcoming events
        </Text>
        <CalendarAccountsPreview />
      </Flex>
      <Box px={'lg'}>
        <UnderlineTabs
          mode="local"
          variant="bordered"
          value={tabName}
          onChange={onTabChange}
          tabs={[
            { value: tabNameConstants.CLIENT, label: 'Clients only' },
            { value: tabNameConstants.ALL, label: 'All meetings' },
          ]}
        />
      </Box>

      <Box style={{ flex: 1, minHeight: 0 }}>
        <ScrollArea h="100%" px="lg">
          <EventsList
            events={events}
            emptyMessage={
              connecting
                ? undefined
                : 'Schedule a meeting with the client to see it here or connect more accounts.'
            }
            renderCard={event => (
              <UpcomingEventCard
                event={event}
                onClick={() => onEventSelect(event.id)}
                isSelected={selectedEventId === event.id}
                onRemove={() => {
                  if (event.id === selectedEventId) {
                    onEventSelect(null);
                  }
                  onRefetchNeeded();
                }}
              />
            )}
          />
          <EventsLoadingBanner />
          {hasNextPage && (
            <Box ta="center" mt="md" mb="md">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading more...' : 'Load More'}
              </Button>
            </Box>
          )}
        </ScrollArea>
      </Box>
    </>
  );
};
